import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList, RotateCcw } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** Invalidated together: fixing a task changes both the queue and the savings total. */
export function invalidateFaultWork(queryClient) {
  queryClient.invalidateQueries({ queryKey: ["faultTasks"] });
  queryClient.invalidateQueries({ queryKey: ["faultSavings"] });
}

/** Work queue: who claimed which costly fault, and what the fix saved. */
export function FaultTasks() {
  const queryClient = useQueryClient();
  const { data, err } = useApiQuery(["faultTasks"], "/faults/tasks");

  const setStatus = async (task, status) => {
    try {
      await api.patch(`/faults/tasks/${task.task_id}`, { status });
      toast.success(status === "fixed" ? "Marked as fixed" : "Reopened");
      invalidateFaultWork(queryClient);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || e.message));
    }
  };

  if (err || !data || data.tasks.length === 0) return null;

  return (
    <div className="mt-6 k-card p-6" data-testid="fault-tasks">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="k-label flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5" /> Fault to-do</div>
          <p className="mt-1 k-sublabel">
            {data.open_count} in progress · {data.fixed_count} fixed
          </p>
        </div>
        {data.saved_total > 0 && (
          <div className="text-sm text-[#4D4D4D]" data-testid="fault-tasks-saved">
            Saved so far <span className="font-heading font-bold text-[#156F5B]">{money(data.saved_total)}</span>
          </div>
        )}
      </div>

      <div className="mt-5 divide-y divide-[#F0F2F1]">
        {data.tasks.map((t) => (
          <div key={t.task_id} className="py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`fault-task-${t.task_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-heading font-semibold text-[#212121] truncate">{t.rule}</div>
              <div className="k-sublabel mt-0.5">
                {t.site_dis} · claimed by {t.claimed_by}
                {t.status === "fixed" && t.saved != null && (
                  <span style={{ color: t.saved > 0 ? "#156F5B" : "#B91C1C" }}>
                    {" "}· {t.saved > 0 ? `${money(t.saved)} saved` : "no change yet"}
                  </span>
                )}
                {t.status === "fixed" && t.still_active && " · still reporting"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#4D4D4D]">{money(t.cost_at_claim)} at claim</span>
              {t.status === "fixed" ? (
                <>
                  <span className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider text-[#156F5B] bg-[#156F5B]/10 border border-[#156F5B]/20 rounded-full px-2 py-0.5">
                    <CheckCircle2 className="h-3 w-3" /> Fixed
                  </span>
                  <button onClick={() => setStatus(t, "claimed")} className="k-btn-outline" data-testid={`task-reopen-${t.task_id}`}>
                    <RotateCcw className="h-3 w-3" /> Reopen
                  </button>
                </>
              ) : (
                <button onClick={() => setStatus(t, "fixed")} className="k-btn-primary" data-testid={`task-fix-${t.task_id}`}>
                  Mark fixed
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
